/**
 * DYNAMIC FOOTER
 * NAP block for SEO, Dr. Reddy online links, complete site navigation
 * Adapts branding, links, and theme based on current site
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, ExternalLink, Clock, Star } from 'lucide-react';
import { useSite } from '../contexts/SiteContext';

export const DynamicFooter = () => {
  const { 
    title, 
    domain, 
    theme, 
    conditions, 
    doctor, 
    bodyPart,
    isMainSite,
    getRelatedSites,
    allSites,
    id: currentSiteId
  } = useSite();

  const relatedSites = getRelatedSites();
  const currentYear = new Date().getFullYear();

  // Dr. Reddy online profile links
  const drReddyOnlineLinks = [
    { name: 'Apollo Hospitals Profile', url: doctor.externalProfiles?.apolloProfile, icon: '🏥' },
    { name: 'Practo', url: doctor.externalProfiles?.practo, icon: '💊' },
    { name: 'Justdial', url: doctor.externalProfiles?.justdial, icon: '📞' },
    { name: 'Instagram @drdnortho', url: doctor.externalProfiles?.instagram, icon: '📸' },
  ].filter(link => link.url);

  return (
    <footer className="bg-gray-900 text-white">
      {/* NAP Block - Critical for Local SEO */}
      <div className="border-b border-gray-800">
        <div className="container mx-auto px-4 py-8">
          <div className="grid md:grid-cols-3 gap-8 items-start">
            {/* Doctor Info */}
            <div className="flex items-start gap-4">
              <img 
                src={doctor.doctorImage} 
                alt={doctor.name}
                className="w-20 h-20 rounded-full object-cover border-2"
                style={{ borderColor: theme.primaryHex }}
              />
              <div>
                <h3 className="font-bold text-lg">{doctor.name}</h3>
                <p className="text-gray-400 text-sm">{doctor.shortTitle || 'Shoulder & Elbow Surgeon'}</p>
                <p className="text-gray-500 text-xs mt-1">{doctor.shortCredentials}</p>
                <div className="flex items-center gap-1 mt-2">
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <span className="text-sm text-yellow-400">{doctor.rating}</span>
                  <span className="text-gray-500 text-xs ml-1">({doctor.reviewCount || 250}+ reviews)</span>
                </div>
              </div>
            </div>

            {/* NAP - Name, Address, Phone */}
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2" style={{ color: theme.primaryHex }}>
                <MapPin className="w-4 h-4" />
                Consultation Location
              </h4>
              <address className="not-italic text-gray-400 text-sm space-y-2">
                <p className="font-medium text-white">{doctor.hospitalFull}</p>
                <p>{doctor.address}</p>
                <a 
                  href={doctor.googleMapsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm mt-2 hover:text-white transition-colors"
                  style={{ color: theme.primaryHex }}
                >
                  Get Directions on Google Maps
                  <ExternalLink className="w-3 h-3" />
                </a>
              </address>
            </div>

            {/* Contact & Timings */}
            <div>
              <h4 className="font-semibold mb-3 flex items-center gap-2" style={{ color: theme.primaryHex }}>
                <Clock className="w-4 h-4" />
                Consultation Hours
              </h4>
              <div className="text-gray-400 text-sm space-y-2">
                <p className="font-medium text-white">{doctor.timings}</p>
                <p className="text-gray-500">By Appointment Only</p>
                <div className="pt-2 space-y-1">
                  <a 
                    href={`tel:${doctor.phone}`}
                    className="flex items-center gap-2 hover:text-white transition-colors"
                  >
                    <Phone className="w-4 h-4" style={{ color: theme.primaryHex }} />
                    {doctor.phone}
                  </a>
                  <a 
                    href={`mailto:${doctor.email}`}
                    className="flex items-center gap-2 hover:text-white transition-colors"
                  >
                    <Mail className="w-4 h-4" style={{ color: theme.primaryHex }} />
                    {doctor.email}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer Links */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid md:grid-cols-5 gap-8">
          {/* Brand & Description */}
          <div className="md:col-span-1">
            <div className="text-2xl font-bold mb-4">
              <span style={{ color: theme.primaryHex }}>{title.split('.')[0]}</span>
              <span className="text-gray-400">.{domain.split('.').slice(1).join('.')}</span>
            </div>
            <p className="text-gray-400 text-sm mb-4">
              Your complete {bodyPart.toLowerCase()} care resource by {doctor.name}, {doctor.shortTitle || 'Shoulder & Elbow Surgeon'} at {doctor.hospitalFull}, Hyderabad.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold mb-4" style={{ color: theme.primaryHex }}>Quick Links</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
              <li><Link to="/about" className="hover:text-white transition-colors">About Dr. Reddy</Link></li>
              <li><Link to="/faq" className="hover:text-white transition-colors">FAQ</Link></li>
              <li><Link to="/blog" className="hover:text-white transition-colors">Health Blog</Link></li>
              <li><Link to="/contact" className="hover:text-white transition-colors">Contact Us</Link></li>
            </ul>
          </div>

          {/* Conditions - Only for specialty sites */}
          {!isMainSite() && conditions?.length > 0 && (
            <div>
              <h4 className="font-semibold mb-4" style={{ color: theme.primaryHex }}>{bodyPart} Conditions</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                {conditions.slice(0, 8).map((condition) => (
                  <li key={condition.id}>
                    <Link to={condition.slug} className="hover:text-white transition-colors">
                      {condition.title}
                    </Link>
                  </li>
                ))}
                {conditions.length > 8 && (
                  <li>
                    <Link to="/shoulder-pain" className="hover:text-white transition-colors" style={{ color: theme.primaryHex }}>
                      View All Conditions →
                    </Link>
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* Our Specialties */}
          <div>
            <h4 className="font-semibold mb-4" style={{ color: theme.primaryHex }}>Our Specialties</h4>
            <ul className="space-y-3 text-sm text-gray-400">
              {relatedSites.map((site) => (
                <li key={site.id}>
                  <a 
                    href={`https://${site.domain}`}
                    className="flex items-center gap-2 hover:text-white transition-colors"
                  >
                    <span 
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: site.theme.primaryHex }}
                    ></span>
                    {site.bodyPart || site.title}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
              ))}
              {/* Current site indicator */}
              <li className="flex items-center gap-2 text-white">
                <span 
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: theme.primaryHex }}
                ></span>
                {bodyPart} (You are here)
              </li>
            </ul>
          </div>

          {/* Dr. Reddy Online - External Profile Links */}
          <div>
            <h4 className="font-semibold mb-4" style={{ color: theme.primaryHex }}>Dr. Reddy Online</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              {drReddyOnlineLinks.map((link, index) => (
                <li key={index}>
                  <a 
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 hover:text-white transition-colors"
                  >
                    <span>{link.icon}</span>
                    {link.name}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </li>
              ))}
              <li className="pt-2 text-xs text-gray-500">
                Read 250+ patient reviews on Justdial and Practo
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Cross-site navigation bar */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            {Object.values(allSites).map((site) => (
              <a
                key={site.id}
                href={`https://${site.domain}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                  site.id === currentSiteId 
                    ? 'bg-white/10 text-white' 
                    : 'text-gray-500 hover:text-white hover:bg-white/5'
                }`}
              >
                <span 
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: site.theme.primaryHex }}
                ></span>
                {site.domain}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row justify-between items-center text-sm text-gray-500">
            <p>© {currentYear} {doctor.name}. All rights reserved.</p>
            <div className="flex gap-4 mt-2 md:mt-0">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
              <Link to="/disclaimer" className="hover:text-white transition-colors">Medical Disclaimer</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
